import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  FormLabel,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import StorefrontIcon from '@mui/icons-material/Storefront';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const IntegrationsPage = () => {
  const [companyId, setCompanyId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [credentialFields, setCredentialFields] = useState([]);
  const [credentials, setCredentials] = useState({});
  const [storeName, setStoreName] = useState('');
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [syncSchedule, setSyncSchedule] = useState('manual');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});

  // Get auth token
  const getAuthToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || '';
  }, []);

  // Load initial data
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const uid = session?.user?.id;
        if (!uid) return;
        setUserId(uid);
        
        // Get company
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('company_id')
          .eq('user_id', uid)
          .maybeSingle();
        if (profile?.company_id) {
          setCompanyId(profile.company_id);
        }
        
        // Load platforms
        await loadPlatforms();
        
        // Load existing integrations
        await loadIntegrations();
        
      } catch (e) {
        console.error('Init error:', e);
        setError('Failed to initialize');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const loadPlatforms = async () => {
    try {
      // Use RPC function to get available platforms
      const { data, error } = await supabase.rpc('get_available_platforms');
      if (error) throw error;
      setPlatforms(data || []);
    } catch (e) {
      console.warn('Failed to load platforms via RPC:', e);
      // Fallback: try market-api if RPC fails
      try {
        const token = await getAuthToken();
        const resp = await fetch(`${BACKEND_URL}/market-api/platforms/available`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setPlatforms(data || []);
        }
      } catch (_) {
        console.warn('Market API also failed');
      }
    }
  };

  const loadIntegrations = async () => {
    try {
      const token = await getAuthToken();
      const resp = await fetch(`${BACKEND_URL}/market-api/integrations/connected`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setIntegrations(data || []);
      }
    } catch (e) {
      console.warn('Failed to load integrations:', e);
    }
  };

  const loadCredentialFields = async (platformCode) => {
    try {
      // Use RPC function to get credential fields
      const { data, error } = await supabase.rpc('get_platform_credential_fields', {
        p_platform_code: platformCode
      });
      if (error) throw error;
      
      setCredentialFields(data || []);
      // Initialize credentials object
      const creds = {};
      (data || []).forEach(f => {
        creds[f.field_key] = '';
      });
      setCredentials(creds);
    } catch (e) {
      console.warn('Failed to load credential fields via RPC:', e);
      // Fallback to market-api
      try {
        const token = await getAuthToken();
        const resp = await fetch(`${BACKEND_URL}/market-api/platforms/${platformCode}/fields`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setCredentialFields(data.fields || []);
          const creds = {};
          (data.fields || []).forEach(f => {
            creds[f.field_key] = '';
          });
          setCredentials(creds);
        }
      } catch (_) {
        console.error('Market API also failed for credential fields');
      }
    }
  };

  // Check if platform supports OAuth (Public App flow)
  const supportsOAuth = (platformCode) => {
    // Platforms that use OAuth authorization code flow
    return ['ikas'].includes(platformCode);
  };

  const handleConnectOAuth = async (platform) => {
    setError('');
    try {
      const token = await getAuthToken();
      const resp = await fetch(`${BACKEND_URL}/market-api/oauth/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          platform_code: platform.code,
          store_name: `My ${platform.name} Store`
        })
      });
      
      const data = await resp.json();
      
      if (!resp.ok) {
        throw new Error(data.detail || data.error || 'Failed to initiate OAuth');
      }
      
      // Redirect to platform authorization page
      window.location.href = data.authorization_url;
      
    } catch (e) {
      setError(e.message || 'Failed to connect');
    }
  };

  const handleOpenDialog = async (platform) => {
    // For OAuth platforms, initiate OAuth flow instead of showing credentials dialog
    if (supportsOAuth(platform.code)) {
      await handleConnectOAuth(platform);
      return;
    }
    
    // For manual credential platforms, show dialog
    setSelectedPlatform(platform);
    setStoreName('');
    setAutoSyncEnabled(false);
    setSyncSchedule('manual');
    setTestResult(null);
    setError('');
    setSuccess('');
    setShowPasswords({});
    await loadCredentialFields(platform.code);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPlatform(null);
    setCredentialFields([]);
    setCredentials({});
    setTestResult(null);
  };

  const handleCredentialChange = (key, value) => {
    setCredentials(prev => ({ ...prev, [key]: value }));
    setTestResult(null); // Reset test result when credentials change
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setError('');
    
    try {
      const token = await getAuthToken();
      const resp = await fetch(`${BACKEND_URL}/market-api/integrations/test-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          platform_code: selectedPlatform.code,
          credentials: credentials
        })
      });
      
      const data = await resp.json();
      
      if (resp.ok && data.success) {
        setTestResult({ success: true, message: 'Connection successful!' });
      } else {
        setTestResult({ success: false, message: data.error || data.detail || 'Connection failed' });
      }
    } catch (e) {
      setTestResult({ success: false, message: e.message || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      // Validate
      if (!storeName.trim()) {
        throw new Error('Store name is required');
      }
      
      // Check required fields
      for (const field of credentialFields) {
        if (field.is_required && !credentials[field.field_key]) {
          throw new Error(`${field.field_label} is required`);
        }
      }
      
      const token = await getAuthToken();
      const resp = await fetch(`${BACKEND_URL}/market-api/integrations/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          platform_code: selectedPlatform.code,
          store_name: storeName,
          credentials: credentials,
          auto_sync_enabled: autoSyncEnabled,
          sync_schedule: syncSchedule
        })
      });
      
      const data = await resp.json();
      
      if (!resp.ok) {
        throw new Error(data.detail || data.error || 'Failed to create integration');
      }
      
      setSuccess('Integration created successfully!');
      handleCloseDialog();
      await loadIntegrations();
      
    } catch (e) {
      setError(e.message || 'Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshIntegration = async (integrationId) => {
    try {
      const token = await getAuthToken();
      await fetch(`${BACKEND_URL}/market-api/integrations/${integrationId}/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadIntegrations();
    } catch (e) {
      console.error('Failed to refresh integration:', e);
    }
  };

  const handleTriggerImport = async (integrationId) => {
    try {
      const token = await getAuthToken();
      const resp = await fetch(`${BACKEND_URL}/market-api/integrations/${integrationId}/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      });
      
      if (resp.ok) {
        setSuccess('Product import started! Check back in a few minutes.');
        await loadIntegrations();
      } else {
        const data = await resp.json();
        setError(data.detail || 'Failed to start import');
      }
    } catch (e) {
      setError(e.message || 'Failed to trigger import');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <CircularProgress size={20} />;
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h4" gutterBottom>
          E-commerce Integrations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Connect your e-commerce platforms to enable AI-powered order lookups and product imports.
        </Typography>
      </Box>
      
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      
      {/* Available Platforms */}
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        Available Platforms
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {platforms.map((platform) => (
          <Grid item xs={12} sm={6} md={4} key={platform.id}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <StorefrontIcon color="primary" />
                  <Typography variant="h6">{platform.name}</Typography>
                </Box>
                {platform.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {platform.description}
                  </Typography>
                )}
                {platform.has_adapter ? (
                  <Chip label="Ready" color="success" size="small" sx={{ mt: 1 }} />
                ) : (
                  <Chip label="Coming Soon" color="default" size="small" sx={{ mt: 1 }} />
                )}
              </CardContent>
              <CardActions>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog(platform)}
                  disabled={!platform.has_adapter}
                  color={supportsOAuth(platform.code) ? 'primary' : 'inherit'}
                  variant={supportsOAuth(platform.code) ? 'contained' : 'text'}
                >
                  {supportsOAuth(platform.code) ? 'Connect with ' + platform.name : 'Configure'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      <Divider sx={{ my: 3 }} />
      
      {/* Connected Integrations */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">
          Connected Stores
        </Typography>
        <IconButton onClick={loadIntegrations} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>
      
      {integrations.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No connected stores yet. Click "Connect" on a platform above to get started.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {integrations.map((integration) => (
            <Grid item xs={12} md={6} key={integration.store_id}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="h6">{integration.store_name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {integration.platform_name}
                      </Typography>
                    </Box>
                    {getStatusIcon(integration.connection_status)}
                  </Box>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Products:</strong> {integration.product_count || 0}
                    </Typography>
                    {integration.last_sync_at && (
                      <Typography variant="body2">
                        <strong>Last Sync:</strong> {new Date(integration.last_sync_at).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<RefreshIcon />}
                    onClick={() => handleRefreshIntegration(integration.store_id)}
                  >
                    Test
                  </Button>
                  <Button 
                    size="small" 
                    startIcon={<SyncIcon />}
                    onClick={() => handleTriggerImport(integration.store_id)}
                    disabled={integration.connection_status !== 'connected'}
                  >
                    Import Products
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Add Integration Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Connect {selectedPlatform?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            <TextField
              fullWidth
              label="Store Name"
              placeholder="My Store"
              value={storeName}
              onChange={(e) => setStoreName(e.target.value)}
              sx={{ mb: 2 }}
              required
              helperText="A friendly name to identify this store"
            />
            
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Credentials</Typography>
            
            {credentialFields.map((field) => (
              <TextField
                key={field.field_key}
                fullWidth
                label={field.field_label}
                placeholder={field.placeholder}
                helperText={field.help_text}
                type={field.field_type === 'password' && !showPasswords[field.field_key] ? 'password' : 'text'}
                value={credentials[field.field_key] || ''}
                onChange={(e) => handleCredentialChange(field.field_key, e.target.value)}
                required={field.is_required}
                sx={{ mb: 2 }}
                InputProps={field.field_type === 'password' ? {
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPasswords(prev => ({ ...prev, [field.field_key]: !prev[field.field_key] }))}
                      edge="end"
                    >
                      {showPasswords[field.field_key] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  )
                } : undefined}
              />
            ))}
            
            {/* Test Connection Button */}
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                onClick={handleTestConnection}
                disabled={testing}
                startIcon={testing ? <CircularProgress size={16} /> : <RefreshIcon />}
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </Button>
              {testResult && (
                <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mt: 1 }}>
                  {testResult.message}
                </Alert>
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Auto-Sync Settings</Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={autoSyncEnabled}
                  onChange={(e) => setAutoSyncEnabled(e.target.checked)}
                />
              }
              label="Enable automatic product sync"
            />
            
            {autoSyncEnabled && (
              <FormControl component="fieldset" sx={{ mt: 2 }}>
                <FormLabel component="legend">Sync Schedule</FormLabel>
                <RadioGroup
                  value={syncSchedule}
                  onChange={(e) => setSyncSchedule(e.target.value)}
                >
                  <FormControlLabel value="on_connect" control={<Radio />} label="Import now (once)" />
                  <FormControlLabel value="daily" control={<Radio />} label="Daily" />
                  <FormControlLabel value="hourly" control={<Radio />} label="Hourly" />
                </RadioGroup>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saving || !testResult?.success}
          >
            {saving ? <CircularProgress size={20} /> : 'Save Integration'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default IntegrationsPage;
